import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  GroupedLessonsOutput,
  LessonAdminQuery,
  LessonInput,
  LessonOutput,
  MutationCreateLessonArgs,
} from '../gql/graphql';
import { graphqlFetch } from '@/utils/graphqlFetch';
import { GET_LESSONS, GET_SINGLE_LESSON } from '../queries/lesson';
import {
  CREATE_LESSON,
  REMOVE_LESSON,
  UPDATE_LESSON,
} from '../mutations/lesson';
import { LessonInputUpdate, MutationUpdateLessonArgs } from '@/api/gql/graphql';

export const useGetLessons = (query: LessonAdminQuery) => {
  return useQuery<GroupedLessonsOutput[], Error>({
    queryKey: ['lessons'],
    queryFn: async () => {
      const data = await graphqlFetch<
        { getLessonsForAdmin: GroupedLessonsOutput[] },
        { query: LessonAdminQuery }
      >(GET_LESSONS, { query });
      return data.getLessonsForAdmin;
    },
    enabled: !!query,
  });
};

export const useGetSingleLessons = (getLessonId: string) => {
  return useQuery<LessonOutput, Error>({
    queryKey: ['lesson', getLessonId],
    queryFn: async () => {
      const data = await graphqlFetch<
        { getLesson: LessonOutput },
        { getLessonId: string }
      >(GET_SINGLE_LESSON, { getLessonId });
      return data.getLesson;
    },
    enabled: !!getLessonId,
  });
};

export const useCreateLesson = () => {
  const queryClient = useQueryClient();
  return useMutation<LessonOutput, Error, LessonInput>({
    mutationFn: async (input) => {
      const data = await graphqlFetch<
        {
          createLesson: LessonOutput;
        },
        MutationCreateLessonArgs
      >(CREATE_LESSON, { input });
      return data.createLesson;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['lessons'],
      }),
  });
};
export const useUpdateLesson = () => {
  const queryClient = useQueryClient();
  return useMutation<
    LessonInputUpdate,
    Error,
    { input: LessonInputUpdate; id: string }
  >({
    mutationFn: async ({ input, id }) => {
      const data = await graphqlFetch<
        {
          updateLesson: LessonInputUpdate;
        },
        MutationUpdateLessonArgs
      >(UPDATE_LESSON, { id, input });
      return data.updateLesson;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lessons'] });
      queryClient.invalidateQueries({
        queryKey: ['lesson', variables.id],
      });
    },
  });
};

export const useRemoveLesson = () => {
  const queryClient = useQueryClient();
  return useMutation<boolean, Error, { removeLessonId: string }>({
    mutationFn: async ({ removeLessonId }) => {
      await graphqlFetch(REMOVE_LESSON, { removeLessonId });
      return true;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['lessons'],
      }),
  });
};
